<template>
  <div class="report-list">
    <s-table
      class="report-list__table"
      :items="reports"
      :fields="fields"
      :sort-by="sortBy.field"
      :sort-desc="sortBy.desc"
      :busy="loading"
      @sort-changed="sort($event)"
    >
      <template #cell(period)="data">
        <span v-if="isPeriodPersonalized(data.item.period)">
          {{ data.item.initialDate | dateTimeFilter }}
          - {{ data.item.finalDate | dateTimeFilter }}
        </span>
        <span v-else>
          <span>{{ $t('reports.period.last-days', {
              days: data.item.period
            })}}
          </span>
          <info-badge
            :string-key="$t('reports.data-history.list.period-tooltip', {
              days: data.item.period
            })"
          />
        </span>
      </template>
      <template #cell(actions)="data">
        <router-link
          :to="getReportRoute(data.item.id)"
          target="_blank"
        >
          <b-button
            size="sm"
            variant="primary"
          >
            {{ $t('general.view') }}
          </b-button>
        </router-link>
        <b-dropdown
          right
          no-caret
          offset="-15"
          variant="link"
          class="report-list__options-btn"
          boundary="window"
        >
          <template slot="button-content">
            <s-loader v-if="isReportLoading(data.item.id)"/>
            <i
              v-else
              aria-hidden="true"
              class="fas fa-ellipsis-v text-gray"
            />
          </template>
          <b-dropdown-item
            v-for="({
              label,
              itemClass,
              iconClass,
              click,
            }) in dropdownItems"
            :key="label"
            :class="itemClass"
            @click="click(data.item)"
          >
            <span
              class="report-list__option-icon text-center"
              :class="iconClass"
            />
            {{ label }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </s-table>
    <grid-paginator
      v-model="pageHandler"
      :per-page="pagination.pageSize"
      :total-rows="pagination.totalRows"
    />
  </div>
</template>

<script>
import GridPaginator from '@/components/lists/Paginator';
import InfoBadge from '@/components/icon/InfoBadge';
import DataHistoryService from '@/domain/services/generalPanel/reports/dataHistory';
import periods from '@/domain/enums/periods';

export default {
  components: {
    InfoBadge,
    GridPaginator,
  },
  props: {
    searchTerm: {
      type: String,
    },
    reports: {
      type: Array,
    },
    loading: {
      type: Boolean,
    },
    sortBy: Object,
    pagination: {
      type: Object,
      default: () => ({
        perPage: 10,
        currentPage: 1,
        totalRows: null,
      }),
    },
  },
  data() {
    return {
      loadingDropdown: '',
    };
  },
  computed: {
    pageHandler: {
      get() {
        return this.pagination.currentPage;
      },
      set(newPage) {
        this.$emit('page-changed', newPage);
      },
    },
    dropdownItems() {
      return [
        {
          itemClass: 'report-list__option-export',
          iconClass: 'fas fa-file-excel',
          label: this.$t('general.export-xlsx'),
          click: this.exportXlsx,
        },
        {
          itemClass: 'report-list__option-export-json',
          iconClass: 'fas fa-download',
          label: this.$t('general.export-json'),
          click: this.exportJSON,
        },
        {
          itemClass: 'report-list__option-edit',
          iconClass: 'fas fa-pencil-alt',
          label: this.$t('reports.edit-report'),
          click: this.editReport,
        },
        {
          itemClass: 'report-list__option-delete',
          iconClass: 'fas fa-trash-alt',
          label: this.$t('reports.delete-report'),
          click: this.confirmDeleteReport,
        },
      ];
    },
    fields() {
      return [
        {
          key: 'reportName',
          sortable: true,
          labelPath: 'report-name',
          class: 'report-list__cell-report-name',
          label: this.$t('reports.report-name'),
        },
        {
          key: 'period',
          sortable: false,
          labelPath: 'period',
          label: this.$t('entities.period'),
          class: 'truncate report-list__cell-period',
        },
        {
          key: 'actions',
          sortable: false,
          labelPath: null,
          label: '',
          class: 'report-list__cell-actions',
        },
      ];
    },
  },
  methods: {
    isReportLoading(id) {
      return this.loadingDropdown === id;
    },
    isPeriodPersonalized(period) {
      return !period || period === periods.PERSONALIZED;
    },
    getReportRoute(id) {
      return {
        name: 'clean-view.data-history.view-report',
        params: { reportId: id },
      };
    },
    exportXlsx(item) {
      this.loadingDropdown = item.id;
      DataHistoryService.exportExcel(item.id)
        .finally(() => { this.loadingDropdown = ''; });
    },
    exportJSON(item) {
      this.loadingDropdown = item.id;
      DataHistoryService.exportJSON(item.id)
        .finally(() => { this.loadingDropdown = ''; });
    },
    editReport(item) {
      this.$router.push({ name: 'reports.data-history.edit-report', params: { id: item.id } });
    },
    viewSummary(item) {
      this.$router.push({ name: 'reports.data-history.edit-report', params: { id: item.id }, query: { tab: 3 } });
    },
    sort(event) {
      if (event.sortBy) {
        this.$emit('sort-changed', event);
      }
    },
    callDeleteReport(item) {
      this.loadingDropdown = item.id;
      DataHistoryService.deleteTemplate(item.id)
        .then(() => {
          this.$pushAlert({ code: 'MS028', mode: 'toast' });
        })
        .finally(() => {
          this.loadingDropdown = '';
          this.$emit('refresh-list');
        });
    },
    confirmDeleteReport(item) {
      this.$confirmMessage({ code: 'MC042' }).accepted(() => {
        this.callDeleteReport(item);
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  .report-list
    display: flex
    flex-direction: column
    ::v-deep
      .btn.dropdown-toggle.btn-link.dropdown-toggle-no-caret:hover,
      .btn.dropdown-toggle.btn-link.dropdown-toggle-no-caret:focus
        text-decoration: none
    &__table
      flex: 1
      min-height: 48vh
    &__options-btn
      margin: -10px 0
    &__option-icon
      margin-right: 5px
    ::v-deep &__option
      &-export a
        color: var(--color-primary)
      &-delete a
        color: var(--color-danger)
    ::v-deep &__cell
      &-report-name
        min-width: 100px
        width: 20%
      &-installations
        width: 25%
      &-asset-type
        width: 25%
      &-asset
        width: 10%
      &-actions
        min-width: 15em
        width: 15%
</style>
